import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, ArrowRight, Eye, EyeOff, Sparkles, Users, Zap, Shield } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/ui/Button';
import SsoButtons from '../components/auth/SsoButtons';
import livePulseLogo from '../assets/live-pulse-logo.png';
import { API_PATH, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import authStyles from '../components/auth/AuthExperience.module.css';
import styles from './SignUp.module.css';

const getPasswordStrength = (pw) => {
    if (!pw) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const map = [
        { level: 0, label: '', color: '' },
        { level: 1, label: 'Weak', color: '#ef4444' },
        { level: 2, label: 'Fair', color: '#f59e0b' },
        { level: 3, label: 'Good', color: '#06b6d4' },
        { level: 4, label: 'Strong', color: '#10b981' },
    ];
    return map[score] || map[0];
};

const SignUp = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const strength = getPasswordStrength(password);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreeToTerms) {
            setError('Please agree to the terms and conditions.');
            return;
        }
        setError('');
        setSuccess('');
        setIsLoading(true);
        try {
            const response = await request(`${API_PATH}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, mobile: mobile.trim() || undefined, password }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Registration failed');
            login(data.token, false, data.user);
            setSuccess('Account created! Redirecting to dashboard…');
            setIsLoading(false);
            setTimeout(() => navigate('/dashboard'), 1800);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const TRUST_ITEMS = [
        { icon: <Users size={16} />, text: 'No app or install for participants' },
        { icon: <Zap size={16} />, text: 'Live results the moment votes come in' },
        { icon: <Shield size={16} />, text: 'Free plan available, no card required' },
    ];
    const LEFT_METRICS = [
        { label: 'Activity types', value: '27' },
        { label: 'Join code', value: '6-digit' },
        { label: 'To get started', value: '₹0' },
    ];
    const HOW_IT_WORKS = [
        { title: 'Share a 6-digit code', desc: 'Or a QR — no app to install' },
        { title: 'Audience responds live', desc: 'From any phone or laptop browser' },
        { title: 'Results appear instantly', desc: 'Charts update the moment votes land' },
    ];

    return (
        <div className={`${styles.page} ${authStyles.page}`}>
            {/* ── Left brand panel ─────────────────────────── */}
            <div className={`${styles.leftPanel} ${authStyles.leftPanel}`}>
                <div className={styles.orb1} />
                <div className={styles.orb2} />
                <div className={styles.orb3} />

                <Link to="/" className={styles.brandLink}>
                    <img src={livePulseLogo} alt="LivePulse" className={styles.brandName} />
                </Link>

                <div className={styles.leftContent}>
                    <div className={styles.leftBadge}>
                        <Sparkles size={13} />
                        <span>Real-time audience intelligence</span>
                    </div>

                    <h2 className={styles.leftTitle}>
                        Start engaging
                        <br />
                        <span className={styles.leftTitleGrad}>your audience today</span>
                    </h2>

                    <p className={styles.leftDesc}>
                        Join thousands of presenters using live polls, Q&A, and quizzes to captivate their audience.
                    </p>

                    <div className={styles.metricStrip}>
                        {LEFT_METRICS.map((item, i) => (
                            <div key={item.label} className={styles.metricItem} style={{ '--delay': `${i * 120}ms` }}>
                                <strong>{item.value}</strong>
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.trustList}>
                        {TRUST_ITEMS.map((item, i) => (
                            <div key={i} className={styles.trustItem} style={{ '--delay': `${240 + i * 120}ms` }}>
                                <div className={styles.trustIcon}>{item.icon}</div>
                                <span>{item.text}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.mockCard}>
                        <div className={styles.mockHeader}>
                            <Zap size={14} className={styles.mockHeaderIcon} />
                            <span className={styles.mockLiveText}>How LivePulse works</span>
                        </div>
                        {HOW_IT_WORKS.map((step, i) => (
                            <div key={step.title} className={styles.stepRow} style={{ '--delay': `${580 + i * 110}ms` }}>
                                <span className={styles.stepNum}>{i + 1}</span>
                                <div className={styles.stepBody}>
                                    <strong className={styles.stepTitle}>{step.title}</strong>
                                    <span className={styles.stepDesc}>{step.desc}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.leftFooter}>© {new Date().getFullYear()} LivePulse. All rights reserved. Design & Developed by <a href="https://ottosoft.in" target="_blank" rel="noopener noreferrer">Ottosoft Private Limited</a></div>
            </div>

            {/* ── Right Form Panel ────────────────────────── */}
            <div className={`${styles.rightPanel} ${authStyles.rightPanel}`}>
                <div className={styles.themeBtn}><ThemeToggle /></div>

                <div className={`${styles.formCard} ${authStyles.formCard}`}>
                    {/* Mobile-only logo */}
                    <Link to="/" className={styles.mobileLogoLink}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.mobileLogoName} />
                    </Link>

                    <div className={styles.formHeader}>
                        <h1 className={styles.formTitle}>Create your account</h1>
                        <p className={styles.formSubtitle}>Start engaging your audience in real-time — free</p>
                    </div>

                    <SsoButtons
                        disabled={isLoading}
                        dividerLabel="or sign up with email"
                        onGoogle={() => {
                            const redirect = `${window.location.origin}/dashboard`;
                            window.location.href = `${API_PATH}/auth/oauth/google?redirect=${encodeURIComponent(redirect)}`;
                        }}
                        onMicrosoft={() => {
                            const redirect = `${window.location.origin}/dashboard`;
                            window.location.href = `${API_PATH}/auth/oauth/microsoft?redirect=${encodeURIComponent(redirect)}`;
                        }}
                    />

                    {error && (
                        <div className={styles.errorBanner}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5a5.5 5.5 0 110-11 5.5 5.5 0 010 11zm-.75-8.25a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 6a.875.875 0 110-1.75.875.875 0 010 1.75z" />
                            </svg>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className={styles.successBanner} role="alert">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                                <path d="M8 0a8 8 0 110 16A8 8 0 018 0zm3.36 5.36a.75.75 0 00-1.06-1.06L6.5 7.94 5.36 6.8a.75.75 0 00-1.06 1.06l1.5 1.5a.75.75 0 001.06 0l3.5-3.5z" />
                            </svg>
                            {success}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formRow}>
                            <div className={styles.formGroup}>
                                <label htmlFor="name" className={styles.label}>Full name</label>
                                <div className={styles.inputWrapper}>
                                    <User size={17} className={styles.inputIcon} />
                                    <input
                                        id="name" type="text" placeholder="Divisha Saloni"
                                        value={name} onChange={(e) => setName(e.target.value)}
                                        className={styles.input} required disabled={isLoading}
                                        autoComplete="name"
                                    />
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="mobile" className={styles.label}>
                                    Mobile <span className={styles.optionalTag}>optional</span>
                                </label>
                                <div className={styles.inputWrapper}>
                                    <Phone size={17} className={styles.inputIcon} />
                                    <input
                                        id="mobile"
                                        type="tel"
                                        placeholder="10-digit number"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                        className={styles.input}
                                        disabled={isLoading}
                                        autoComplete="tel"
                                        maxLength={10}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="email" className={styles.label}>Email address</label>
                            <div className={styles.inputWrapper}>
                                <Mail size={17} className={styles.inputIcon} />
                                <input
                                    id="email" type="email" placeholder="name@company.com"
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    className={styles.input} required disabled={isLoading}
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="password" className={styles.label}>Password</label>
                            <div className={styles.inputWrapper}>
                                <Lock size={17} className={styles.inputIcon} />
                                <input
                                    id="password" type={showPassword ? 'text' : 'password'}
                                    placeholder="Min. 8 characters"
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    className={styles.input} required disabled={isLoading}
                                    minLength={8} autoComplete="new-password"
                                />
                                <button
                                    type="button" className={styles.eyeBtn}
                                    onClick={() => setShowPassword(v => !v)} tabIndex={-1}
                                    aria-label="Toggle password visibility"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            {password && (
                                <div className={styles.strengthBar}>
                                    <div className={styles.strengthTrack}>
                                        {[1, 2, 3, 4].map(i => (
                                            <div
                                                key={i}
                                                className={styles.strengthSegment}
                                                style={{ background: i <= strength.level ? strength.color : undefined }}
                                            />
                                        ))}
                                    </div>
                                    <span className={styles.strengthLabel} style={{ color: strength.color }}>
                                        {strength.label}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className={styles.termsRow}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox" checked={agreeToTerms}
                                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                                    className={styles.checkbox}
                                />
                                <span className={styles.checkmark} />
                                <span>
                                    I agree to the{' '}
                                    <Link to="/terms" rel="noopener noreferrer" className={styles.termsLink}>Terms of Service</Link>
                                    {' '}and{' '}
                                    <Link to="/privacy" rel="noopener noreferrer" className={styles.termsLink}>Privacy Policy</Link>
                                </span>
                            </label>
                        </div>

                        <Button
                            type="submit"
                            className={`${styles.submitBtn} btn-pill-primary`}
                            disabled={isLoading || !agreeToTerms}
                            loading={isLoading}
                            iconRight={!isLoading ? <ArrowRight size={17} /> : null}
                        >
                            {isLoading ? 'Creating account…' : 'Create account'}
                        </Button>
                    </form>

                    <p className={styles.bottomLink}>
                        Already have an account? <Link to="/login">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SignUp;
